/**
 * Deferred background video.
 *
 * Background videos are decoration: the poster carries the first paint and is
 * the LCP element, so the video file must never compete with it. Each
 * <video data-hero-video> therefore ships with no src at all. Once the page has
 * finished loading, and only when the element is near the viewport, the right
 * source is attached and played.
 *
 * A mobile source is used below the lg breakpoint when one is supplied, so
 * phones can be given a lighter file than the desktop master; without one they
 * fall back to the same file rather than losing the video.
 *
 * Two groups never trigger the download at all and keep the poster instead:
 * people who asked for reduced motion, and people on a metered or
 * data-saving connection.
 */

const MOBILE_QUERY = '(max-width: 1023px)';

function sourceFor(video) {
  const mobile = window.matchMedia(MOBILE_QUERY).matches;

  return (mobile && video.dataset.srcMobile) || video.dataset.src || '';
}

function start(video) {
  if (video.dataset.heroLoaded) {
    return;
  }

  const src = sourceFor(video);

  if (!src) {
    return;
  }

  video.dataset.heroLoaded = '1';
  video.src = src;
  video.load();

  // A refused autoplay is a browser policy decision, not an error: the poster
  // stays up and the page is unaffected.
  const played = video.play();

  if (played && typeof played.catch === 'function') {
    played.catch(() => {});
  }
}

function initHeroVideos() {
  const videos = document.querySelectorAll('video[data-hero-video]');

  if (!videos.length) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData === true;

  if (reducedMotion || saveData) {
    return;
  }

  videos.forEach((video) => {
    if (!('IntersectionObserver' in window)) {
      start(video);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            start(video);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' },
    );

    observer.observe(video);
  });
}

// Wait for load, not DOMContentLoaded: everything the page needs to paint
// should already be in before a decorative video asks for bandwidth.
if (document.readyState === 'complete') {
  initHeroVideos();
} else {
  window.addEventListener('load', initHeroVideos);
}
